import { useEffect, useId, useRef, useState } from "react";
import { Search } from "lucide-react";
import type { SearchSuggestion } from "../lib/smartSearch";

export type SmartSearchInputProps = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  inputClassName?: string;
  suggestions?: SearchSuggestion[];
  onSuggestionSelect?: (suggestion: SearchSuggestion) => void;
  /** Show dropdown when query length >= this (default 1) */
  minCharsForSuggestions?: number;
  ariaLabel?: string;
};

export function SmartSearchInput({
  value,
  onChange,
  placeholder = "Search…",
  className = "",
  inputClassName = "",
  suggestions = [],
  onSuggestionSelect,
  minCharsForSuggestions = 1,
  ariaLabel = "Search",
}: SmartSearchInputProps) {
  const listId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  const trimmed = value.trim();
  const showSuggestions =
    open &&
    trimmed.length >= minCharsForSuggestions &&
    suggestions.length > 0;

  useEffect(() => {
    setHighlightedIndex(0);
  }, [value, suggestions]);

  useEffect(() => {
    const onDocMouseDown = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, []);

  const selectSuggestion = (suggestion: SearchSuggestion) => {
    const next = suggestion.value ?? suggestion.label;
    onChange(next);
    onSuggestionSelect?.(suggestion);
    setOpen(false);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((i) => Math.min(i + 1, suggestions.length - 1));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((i) => Math.max(i - 1, 0));
      return;
    }
    if (e.key === "Enter" && suggestions[highlightedIndex]) {
      e.preventDefault();
      selectSuggestion(suggestions[highlightedIndex]);
      return;
    }
    if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={containerRef} className={`relative ${className}`.trim()}>
      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
        <Search className="h-5 w-5 text-gray-400" aria-hidden />
      </div>
      <input
        ref={inputRef}
        type="search"
        autoComplete="off"
        role="combobox"
        aria-label={ariaLabel}
        aria-expanded={showSuggestions}
        aria-controls={showSuggestions ? listId : undefined}
        aria-autocomplete="list"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={
          inputClassName ||
          "block w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-xl leading-5 bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary sm:text-sm transition-all shadow-sm"
        }
      />

      {showSuggestions && (
        <ul
          id={listId}
          role="listbox"
          className="absolute left-0 right-0 top-[calc(100%+4px)] z-50 max-h-56 overflow-y-auto rounded-xl border border-border-light bg-white py-1 shadow-lg"
        >
          {suggestions.map((suggestion, idx) => (
            <li key={suggestion.id} role="option" aria-selected={idx === highlightedIndex}>
              <button
                type="button"
                className={`w-full px-3 py-2.5 text-left transition-colors ${
                  idx === highlightedIndex
                    ? "bg-primary/10 text-primary"
                    : "text-text-main hover:bg-slate-50"
                }`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => selectSuggestion(suggestion)}
              >
                <p className="text-sm font-semibold truncate">{suggestion.label}</p>
                {suggestion.subtitle ? (
                  <p className="text-xs text-text-muted truncate mt-0.5">
                    {suggestion.subtitle}
                  </p>
                ) : null}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
